/**
 * RateSubheader Component
 * Displays live gold and silver rates below the navigation header
 * Sticky positioned with loading, refreshing, and error state handling
 */

import React from 'react';
import { formatRelativeTime } from '../utils/dateFormat';

interface RateSubheaderProps {
  goldRate: number | null;
  silverRate: number | null;
  timestamp: Date | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  usingFallback: boolean;
}

export const RateSubheader: React.FC<RateSubheaderProps> = ({
  goldRate,
  silverRate,
  timestamp,
  loading,
  refreshing,
  error,
  usingFallback,
}) => {
  const lastUpdate = timestamp ? formatRelativeTime(timestamp) : 'N/A';

  return (
    <div className="sticky top-[72px] z-40 bg-gray-900 border-b border-gray-800 py-3 px-4">
      <div className="max-w-7xl mx-auto">
        {loading && (
          <p className="text-yellow-500 text-sm text-center">
            Loading rates...
          </p>
        )}
        
        {refreshing && !loading && (
          <p className="text-yellow-500 text-sm text-center animate-pulse">
            Updating...
          </p>
        )}
        
        {error && !loading && !refreshing && goldRate === null && silverRate === null && (
          <p className="text-yellow-500 text-sm text-center">
            Rates currently unavailable
          </p>
        )}
        
        {!loading && goldRate !== null && silverRate !== null && (
          <div className="flex flex-col md:flex-row justify-center items-center md:space-x-8 space-y-2 md:space-y-0">
            {/* Gold Rate */}
            <div className="flex items-center space-x-2">
              <span className="text-yellow-500 text-sm md:text-base font-medium">LIVE GOLD 24K:</span>
              <span className="text-white font-bold text-lg md:text-xl">
                ₹{goldRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/g
              </span>
            </div>

            {/* Silver Rate */}
            <div className="flex items-center space-x-2">
              <span className="text-yellow-500 text-sm md:text-base font-medium">LIVE SILVER:</span>
              <span className="text-white font-bold text-lg md:text-xl">
                ₹{silverRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/g
              </span>
            </div>

            {/* Last Update */}
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 text-sm md:text-base">
                {usingFallback && '⚠ '}Updated: {lastUpdate}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
